import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { searchKpfUsers } from '../utils/conversationApi';
import './KpfEmailSuggestions.css';

/**
 * Typeahead for "type a KPF colleague's email" boxes (project invite, conversation
 * share): the hook owns the debounced search and the highlighted row, and
 * <KpfSuggestionList> draws the dropdown. Shared so both boxes behave the same
 * and neither has to re-solve the stale-response and clipping problems below.
 */

const SUGGESTION_MIN_CHARS = 2;
const SUGGESTION_DEBOUNCE_MS = 250;

// The id of row `index` in the list `listId` -- what the input's
// aria-activedescendant points at, so a screen reader announces the row the
// arrow keys moved to (focus itself never leaves the input).
const optionId = (listId, index) => `${listId}-option-${index}`;

/**
 * @param {{ exclude?: (candidate: {email: string}) => boolean }} [options]
 *   `exclude` hides a suggestion (e.g. someone already added to the box).
 * @returns {{
 *   suggestions: Array, open: boolean, activeIndex: number, listId: string,
 *   activeOptionId: string|undefined,
 *   search: (text: string) => void, reset: () => void, close: () => void,
 *   reopen: () => void, handleKeyDown: (e: KeyboardEvent, onSelect: Function) => boolean,
 *   freshTop: () => object|null, setActiveIndex: Function,
 * }}
 */
export function useKpfSuggestions({ exclude } = {}) {
  const [raw, setRaw] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  // The text last searched for, and the text the suggestions on screen
  // answer. They differ while a newer search is pending (debounce + request),
  // when the list still shows matches for what was typed a moment ago.
  const queryRef = useRef('');
  const answeredRef = useRef('');
  // One per box, so two boxes on screen never share option ids.
  const listId = `kes-list-${useId().replace(/:/g, '')}`;

  const suggestions = exclude ? raw.filter((c) => !exclude(c)) : raw;

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Invalidates any request still in flight, so a slow response can't
    // reopen the list after the user has cleared or picked something.
    seqRef.current += 1;
    queryRef.current = '';
    answeredRef.current = '';
    setRaw([]);
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const search = useCallback((text) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = (text || '').trim();
    // Debounced + sequence-guarded: a fast typist can have several of these
    // in flight at once, and without the sequence check a slow earlier
    // response arriving after a faster later one would overwrite it with
    // stale suggestions.
    const seq = ++seqRef.current;
    queryRef.current = trimmed;
    if (trimmed.length < SUGGESTION_MIN_CHARS) {
      setRaw([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    timerRef.current = setTimeout(async () => {
      let results;
      try {
        results = await searchKpfUsers(trimmed);
      } catch (err) {
        // Offline, or signed out mid-type: nothing to suggest -- and the
        // previous query's suggestions mustn't stay up as if they matched.
        results = [];
      }
      if (seq !== seqRef.current) return;
      answeredRef.current = trimmed;
      setRaw(results);
      setOpen(results.length > 0);
      setActiveIndex(-1);
    }, SUGGESTION_DEBOUNCE_MS);
  }, []);

  /**
   * The top suggestion, if the list is open and answers what was typed last
   * -- null while a newer search is still on its way, when the top row could
   * be someone the finished query wouldn't match.
   */
  const freshTop = useCallback(() => {
    if (!open || suggestions.length === 0) return null;
    return answeredRef.current === queryRef.current ? suggestions[0] : null;
  }, [open, suggestions]);

  const close = useCallback(() => setOpen(false), []);
  const reopen = useCallback(() => { if (raw.length > 0) setOpen(true); }, [raw.length]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  /**
   * Arrow keys move the highlight, Enter picks the highlighted row, Tab picks
   * the highlighted row or else the top one, Escape closes. Returns true when
   * the key was consumed, so the caller can skip its own handling (e.g. Enter
   * must pick a suggestion, not submit the form). Enter with nothing
   * highlighted is NOT consumed.
   */
  const handleKeyDown = useCallback((e, onSelect) => {
    if (!open || suggestions.length === 0) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      return true;
    }
    if (e.key === 'Enter' && activeIndex >= 0 && suggestions[activeIndex]) {
      e.preventDefault();
      onSelect(suggestions[activeIndex]);
      return true;
    }
    // Tab completes, as in a code editor. With the list still catching up to
    // the typing (freshTop is null) it waits -- focus stays put and the next
    // Tab picks -- rather than add the wrong person or jump out of the box.
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const pick = activeIndex >= 0 ? suggestions[activeIndex] : freshTop();
      if (pick) onSelect(pick);
      return true;
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      return true;
    }
    return false;
  }, [open, suggestions, activeIndex, freshTop]);

  const isOpen = open && suggestions.length > 0;
  return {
    suggestions, open: isOpen, activeIndex, setActiveIndex,
    listId, search, reset, close, reopen, handleKeyDown, freshTop,
    activeOptionId: isOpen && activeIndex >= 0 && activeIndex < suggestions.length
      ? optionId(listId, activeIndex) : undefined,
  };
}

const initialsOf = (candidate) => {
  const source = (candidate.display_name || candidate.email || '').trim();
  const words = source.split(/[\s@.]+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Directory photo for a suggestion, or initials when there is none -- or when
 * the photo fails to load (a colleague without an uploaded picture can still
 * have a URL on file that 404s). Exported for the share box's recipient chips.
 */
export function KpfAvatar({ candidate }) {
  const [failed, setFailed] = useState(false);
  const img = typeof candidate.profile_img === 'string' ? candidate.profile_img.trim() : '';
  if (img && img.toUpperCase() !== 'N/A' && !failed) {
    return (
      <img
        src={img}
        alt=""
        referrerPolicy="no-referrer"
        className="kes-avatar"
        onError={() => setFailed(true)}
      />
    );
  }
  return <span className="kes-avatar kes-avatar--initials" aria-hidden="true">{initialsOf(candidate)}</span>;
}

/**
 * The dropdown. Rendered in a portal on document.body, positioned from the
 * anchor's on-screen rectangle: a modal body that scrolls (overflow-y: auto)
 * would otherwise clip an absolutely positioned child and force the user to
 * scroll to find it. Opens below the anchor, or above when there is more room
 * there. Mount it only while open.
 */
export function KpfSuggestionList({ anchorRef, suggestions, activeIndex, onSelect, onHover, listId }) {
  const [style, setStyle] = useState(null);

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const margin = 12;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const flip = spaceBelow < 180 && spaceAbove > spaceBelow;
    setStyle({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(120, Math.min(280, flip ? spaceAbove : spaceBelow)),
      ...(flip ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
    // Capture phase so scrolling the modal body (not just the window) re-anchors it.
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition, suggestions]);

  // Keeps the keyboard-highlighted row in view inside a scrolling list.
  const activeRef = useRef(null);
  useEffect(() => {
    if (activeIndex >= 0 && activeRef.current) activeRef.current.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!style || suggestions.length === 0) return null;

  return createPortal(
    <ul
      id={listId}
      className="kes-list"
      role="listbox"
      style={style}
      // Grabbing the list's scrollbar must not blur the input and close it.
      onMouseDown={(e) => e.preventDefault()}
    >
      {suggestions.map((candidate, index) => (
        <li
          key={candidate.email}
          id={optionId(listId, index)}
          ref={index === activeIndex ? activeRef : null}
          role="option"
          aria-selected={index === activeIndex}
          className={`kes-item${index === activeIndex ? ' kes-item--active' : ''}`}
          // preventDefault keeps focus on the input, so this fires instead of
          // the input's onBlur closing the list first.
          onMouseDown={(e) => { e.preventDefault(); onSelect(candidate); }}
          onMouseEnter={onHover ? () => onHover(index) : undefined}
        >
          <KpfAvatar candidate={candidate} />
          <span className="kes-text">
            <span className="kes-name">{candidate.display_name || candidate.email}</span>
            {candidate.display_name && <span className="kes-email">{candidate.email}</span>}
          </span>
        </li>
      ))}
    </ul>,
    document.body,
  );
}
